import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SelectWithCreate } from '../../components/ui/select-with-create';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import {
  Network,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Copy,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Box,
  PackageOpen,
  Check,
  X,
  Activity,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const Switches = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'switches');

  const [switches, setSwitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, stock: 0 });
  const [formOptions, setFormOptions] = useState({ sites: [], ubicaciones: [], modelos: [], estados: [] });
  
  // Filter options (only values that exist in switches table)
  const [filterOptions, setFilterOptions] = useState({ sites: [], ubicaciones: [], modelos: [], estados: [] });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  
  // Filters
  const [filters, setFilters] = useState({ search: '', site_id: '', modelo_id: '', ubicacion_id: '', status_id: '', stock_filter: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para búsqueda global
  
  // Active stat filter (for visual feedback)
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(switches);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '', ubicacion: '', modelo: '', site: '', status_id: '', ip: '', nro_serie: '',
    firmware: '', loopback: false, port_security: false, radius: false,
    general_monitoring: false, specific_monitoring: false, ro_community: '',
    costo: '', fecha_compra: '', observations: ''
  });

  const fetchSwitches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.modelo_id) params.append('modelo_id', filters.modelo_id);
      if (filters.ubicacion_id) params.append('ubicacion_id', filters.ubicacion_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.stock_filter) params.append('stock_filter', filters.stock_filter);
      
      const response = await api.get(`/assets/switches?${params}`);
      setSwitches(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar switches');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/switches/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/switches/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/switches/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => { fetchFormOptions(); fetchFilterOptions(); fetchStats(); }, []);
  useEffect(() => { fetchSwitches(); }, [pagination.page, pagination.limit, sortConfig]);
  
  // Debounce para búsqueda global (800ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 800);
    return () => clearTimeout(t);
  }, [searchInput]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchSwitches();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  // Stat card click handler - filters the table
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setSearchInput(''); // Limpiar búsqueda global
    
    // Reset filters first
    let newFilters = { search: '', site_id: '', modelo_id: '', ubicacion_id: '', status_id: '', stock_filter: '' };
    
    switch (filterType) {
      case 'en_uso':
        newFilters.stock_filter = 'en_uso';
        break;
      case 'stock':
        newFilters.stock_filter = 'stock';
        break;
      default:
        // 'all' - keep filters empty
        break;
    }
    
    setFilters(newFilters);
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '', ubicacion: '', modelo: '', site: '', status_id: '', ip: '', nro_serie: '',
      firmware: '', loopback: false, port_security: false, radius: false,
      general_monitoring: false, specific_monitoring: false, ro_community: '',
      costo: '', fecha_compra: '', observations: ''
    });
    setModalOpen(true);
  };

  const handleEdit = async (sw) => {
    try {
      const response = await api.get(`/assets/switches/${sw.id}`);
      const data = response.data;
      setEditingItem(data);
      setFormData({
        name: data.name || '',
        ubicacion: data.ubicacion?.id?.toString() || '',
        modelo: data.modelo?.id?.toString() || '',
        site: data.site?.id?.toString() || '',
        status_id: data.status_id?.toString() || '',
        ip: data.ip || '',
        nro_serie: data.nro_serie || '',
        firmware: data.firmware || '',
        loopback: data.loopback || false,
        port_security: data.port_security || false,
        radius: data.radius || false,
        general_monitoring: data.general_monitoring || false,
        specific_monitoring: data.specific_monitoring || false,
        ro_community: data.ro_community || '',
        costo: data.costo?.toString() || '',
        fecha_compra: data.fecha_compra || '',
        observations: data.observations || ''
      });
      setModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar switch');
    }
  };

  const handleCopy = async (sw) => {
    try {
      const response = await api.get(`/assets/switches/${sw.id}`);
      const data = response.data;
      setEditingItem(null); // Not editing, creating new
      setFormData({
        name: '', // Clear unique field
        ubicacion: data.ubicacion?.id?.toString() || '',
        modelo: data.modelo?.id?.toString() || '',
        site: data.site?.id?.toString() || '',
        status_id: data.status_id?.toString() || '',
        ip: '', // Clear unique field
        nro_serie: '', // Clear unique field
        firmware: data.firmware || '',
        loopback: data.loopback || false,
        port_security: data.port_security || false,
        radius: data.radius || false,
        general_monitoring: data.general_monitoring || false,
        specific_monitoring: data.specific_monitoring || false,
        ro_community: data.ro_community || '',
        costo: data.costo?.toString() || '',
        fecha_compra: '',
        observations: ''
      });
      setModalOpen(true);
      toast.info('Datos copiados. Complete los campos unicos.');
    } catch (error) {
      toast.error('Error al copiar switch');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.ubicacion || !formData.modelo || !formData.site) {
      toast.error('Ubicacion, Modelo y Site son requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        ubicacion: parseInt(formData.ubicacion),
        modelo: parseInt(formData.modelo),
        site: parseInt(formData.site),
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        ip: formData.ip || null,
        nro_serie: formData.nro_serie || null,
        firmware: formData.firmware || null,
        loopback: formData.loopback,
        port_security: formData.port_security,
        radius: formData.radius,
        general_monitoring: formData.general_monitoring,
        specific_monitoring: formData.specific_monitoring,
        ro_community: formData.ro_community || null,
        costo: formData.costo ? parseFloat(formData.costo) : null,
        fecha_compra: formData.fecha_compra || null,
        observations: formData.observations || null
      };

      if (editingItem) {
        await api.put(`/assets/switches/${editingItem.id}`, payload);
        toast.success('Switch actualizado exitosamente');
      } else {
        await api.post('/assets/switches', payload);
        toast.success('Switch creado exitosamente');
      }
      setModalOpen(false);
      fetchSwitches();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sw) => {
    try {
      await api.delete(`/assets/switches/${sw.id}`);
      toast.success('Switch eliminado exitosamente');
      setDeleteConfirm(null);
      fetchSwitches();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleMonitoring = async (sw, field) => {
    if (!canUpdate) return;
    try {
      const payload = { [field]: !sw[field] };
      await api.patch(`/assets/switches/${sw.id}/monitoring`, payload);
      // Update local state
      setSwitches(prev => prev.map(s => 
        s.id === sw.id ? { ...s, [field]: !s[field] } : s
      ));
      toast.success('Monitoreo actualizado');
    } catch (error) {
      toast.error('Error al actualizar monitoreo');
    }
  };

  // Boolean badge
  const BoolBadge = ({ value, trueText = "Si", falseText = "No" }) => (
    <span className={`badge ${value ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
      {value ? trueText : falseText}
    </span>
  );

  // Monitoring toggle inline - usando Switch de Shadcn
  const MonitoringToggle = ({ sw, field, label }) => (
    <div className="flex items-center justify-center gap-2">
      <Switch
        checked={sw[field]}
        onCheckedChange={() => handleToggleMonitoring(sw, field)}
        disabled={!canUpdate}
        className="data-[state=checked]:bg-primary"
        data-testid={`switch-${field}-${sw.id}`}
      />
      <span className="text-xs text-muted-foreground">
        {sw[field] ? 'Si' : 'No'}
      </span>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="switches-page">
        {/* Header - Same pattern as Servers/Firewalls */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Switches</h1>
            <p className="page-subtitle">Gestion de switches de red</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/switches"
              filters={{ search: filters.search }}
              filename="switches"
            />
            <Button
              className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={canCreate ? handleCreate : undefined}
              disabled={!canCreate}
              data-testid="add-switch-btn"
            >
              <Plus className="h-4 w-4" />
              Nuevo Switch
            </Button>
          </div>
        </div>

        {/* Global Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar en todos los campos... (mín. 2 caracteres)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-10 bg-card border-border"
            data-testid="global-search"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '' })); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="switches-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Network className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total switches</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'en_uso' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('en_uso')}
            data-testid="stats-active"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.total - stats.stock}</div>
              <div className="stats-card-label">En uso</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeStatFilter === 'stock' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatFilter('stock')}
            data-testid="stats-stock"
          >
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.stock}</div>
              <div className="stats-card-label">En stock</div>
            </div>
          </div>
        </div>

        {/* Table with inline filters - Same pattern as Servers/Firewalls */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip" />
                          </div>
                        </th>
                        <th>MODELO</th>
                        <th className="hidden lg:table-cell">SITE</th>
                        <th className="hidden lg:table-cell">UBICACION</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('status_name')}
                        >
                          <div className="flex items-center gap-1">
                            ESTADO
                            <SortIcon field="status_name" />
                          </div>
                        </th>
                        <th className="w-28 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="w-10 py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-nombre"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.ip || ''}
                            onChange={(e) => setFilters(prev => ({ ...prev, ip: e.target.value }))}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.modelo_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, modelo_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.modelos?.map(m => (
                                <SelectItem key={m.id} value={m.id.toString()}>{m.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Select value={filters.site_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, site_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.sites?.map(s => (
                                <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Select value={filters.ubicacion_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, ubicacion_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.ubicaciones?.map(u => (
                                <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.status_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.estados?.map(e => (
                                <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {switches.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Network className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron switches</p>
                            </div>
                          </td>
                        </tr>
                      ) : switches.map((sw) => (
                        <tr key={sw.id} data-testid={`row-switch-${sw.id}`} className={isSelected(sw.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(sw.id)}
                              onCheckedChange={() => toggleRow(sw.id)}
                              aria-label={`Seleccionar ${sw.name}`}
                            />
                          </td>
                          <td className="font-medium text-primary">
                            <div className="flex items-center gap-1.5">
                              {sw.name}
                              {sw.documented_ports > 0 && (
                                <span 
                                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                  title={`${sw.documented_ports} puerto${sw.documented_ports > 1 ? 's' : ''} documentado${sw.documented_ports > 1 ? 's' : ''}`}
                                >
                                  {sw.documented_ports}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="font-mono text-sm text-muted-foreground">{sw.ip || '-'}</td>
                          <td>{sw.modelo?.name || '-'}</td>
                          <td className="hidden lg:table-cell text-muted-foreground">{sw.site?.name || '-'}</td>
                          <td className="hidden lg:table-cell text-muted-foreground">{sw.ubicacion?.name || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              sw.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : sw.status_name?.toLowerCase().includes('baja') || sw.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {sw.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => navigate(`/assets/switches/${sw.id}`)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-switch-${sw.id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(sw)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title={canUpdate ? "Editar" : "Sin permiso"}
                                data-testid={`edit-switch-${sw.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(sw)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title={canCreate ? "Copiar registro" : "Sin permiso"}
                                data-testid={`copy-switch-${sw.id}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteConfirm(sw)}
                                className={`action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title={canDelete ? "Eliminar" : "Sin permiso"}
                                data-testid={`delete-switch-${sw.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.limit.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8" data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8" data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Network className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Switch' : 'Nuevo Switch'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 grid grid-cols-2 gap-4">
              {/* Row 1 */}
              <div className="col-span-2">
                <Label className="text-sm font-medium">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-subtle mt-1.5"
                  data-validation={!formData.name ? 'required-empty' : ''}
                  placeholder="Nombre del switch"
                  data-testid="input-switch-name"
                />
              </div>
              
              {/* Row 2 */}
              <div>
                <Label className="text-sm font-medium">Site *</Label>
                <SelectWithCreate
                  value={formData.site}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site: v }))}
                  options={formOptions.sites?.map(s => ({ id: s.id, nombre: s.name })) || []}
                  placeholder="Seleccionar site"
                  createEndpoint="/assets/switches/quick-create/site"
                  entityName="Site"
                  createFields={{
                    name: { label: "Nombre", placeholder: "Ej: TZ_NUEVO" },
                    description: { label: "Descripción", placeholder: "Descripción del site (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, site: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Ubicación *</Label>
                <SelectWithCreate
                  value={formData.ubicacion}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, ubicacion: v }))}
                  options={formOptions.ubicaciones?.map(u => ({ id: u.id, nombre: u.name })) || []}
                  placeholder="Seleccionar ubicación"
                  createEndpoint="/assets/switches/quick-create/ubicacion"
                  entityName="Ubicación"
                  createFields={{
                    name: { label: "Ubicación", placeholder: "Ej: DATACENTER" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, ubicacion: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              
              {/* Row 3 */}
              <div>
                <Label className="text-sm font-medium">Modelo *</Label>
                <SelectWithCreate
                  value={formData.modelo}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, modelo: v }))}
                  options={formOptions.modelos?.map(m => ({ id: m.id, nombre: m.name })) || []}
                  placeholder="Seleccionar modelo"
                  createEndpoint="/assets/switches/quick-create/modelo"
                  entityName="Modelo"
                  createFields={{
                    name: { label: "Modelo", placeholder: "Ej: CBS250-48T-4G" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, modelo: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Estado</Label>
                <Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger 
                    className="input-subtle mt-1.5"
                    data-validation={formData.status_id ? 'valid' : ''}
                  >
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.estados?.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">IP</Label>
                <Input
                  value={formData.ip}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="192.168.1.1"
                />
              </div>
              
              {/* Row 4 */}
              <div>
                <Label className="text-sm font-medium">Nro Serie</Label>
                <Input
                  value={formData.nro_serie}
                  onChange={(e) => setFormData(prev => ({ ...prev, nro_serie: e.target.value }))}
                  className="input-subtle mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Firmware</Label>
                <Input
                  value={formData.firmware}
                  onChange={(e) => setFormData(prev => ({ ...prev, firmware: e.target.value }))}
                  className="input-subtle mt-1.5"
                />
              </div>
              
              {/* Row 5 */}
              <div>
                <Label className="text-sm font-medium">RO Community</Label>
                <Input
                  value={formData.ro_community}
                  onChange={(e) => setFormData(prev => ({ ...prev, ro_community: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="SNMP community"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Costo</Label>
                <Input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="0.00"
                />
              </div>
              
              {/* Row 6 */}
              <div>
                <Label className="text-sm font-medium">Fecha Compra</Label>
                <Input
                  type="date"
                  value={formData.fecha_compra}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha_compra: e.target.value }))}
                  className="input-subtle mt-1.5"
                />
              </div>
              <div className="flex flex-col justify-end">
                {/* Empty for alignment */}
              </div>
              
              {/* Observations */}
              <div className="col-span-2">
                <Label className="text-sm font-medium">Observaciones</Label>
                <Input
                  value={formData.observations}
                  onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminacion
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                Desea eliminar el switch <span className="font-semibold text-foreground">{deleteConfirm?.name}</span>?
              </p>
              {deleteConfirm?.ip && (
                <p className="text-sm text-muted-foreground mt-1">IP: {deleteConfirm.ip}</p>
              )}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar - Solo site, NO estado_interno */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchSwitches(); fetchStats(); }}
          endpoint="/assets/switches"
          options={{
            sites: formOptions.sites?.map(s => ({ id: s.id, nombre: s.name })),
            ubicaciones: formOptions.ubicaciones?.map(u => ({ id: u.id, nombre: u.name })),
            statuses: formOptions.estados?.map(e => ({ id: e.id, nombre: e.name || e.nombre }))
          }}
          fields={['site_id', 'ubicacion_id', 'status_id']}
        />
      </div>
    </Layout>
  );
};

export default Switches;
